"use client";
import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { a, useSpring } from "@react-spring/three";
import { scenesData } from "./sceneData";
import { GrNext, GrPrevious } from "react-icons/gr";
import WhiteSpinner from "./components/WhiteSpinner";

const degToRad = (deg) => (deg * Math.PI) / 180;

export default function App() {
  const scenes = scenesData;
  const [activeIndex, setActiveIndex] = useState(0);
  const [history, setHistory] = useState([]);
  const controlsRef = useRef();
  const [rotationSpeed, setRotationSpeed] = useState(-0.2);
  const [isMobile, setIsMobile] = useState(false);

  const { opacity } = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    reset: true,
    reverse: false,
    config: { duration: 800 },
  });

  const handleNext = () => {
    const nextScene = scenes[activeIndex].next;
    if (nextScene) {
      setHistory((prev) => [...prev, activeIndex]);
      setActiveIndex(nextScene);
    }
  };

  const handlePrev = () => {
    if (history.length > 0) {
      const lastScene = history[history.length - 1];

      // Remove last entry from history
      setHistory((prev) => prev.slice(0, prev.length - 1));

      setActiveIndex(lastScene);
    }
  };

  const handleHotspotClick = (spot) => {
    if (spot.type === "navigate") {
      setTimeout(() => {
        setHistory((prevHistory) => [...prevHistory, activeIndex]);
        setActiveIndex(spot.target);
      }, 600);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 500;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setRotationSpeed(isMobile ? -0.3 : -0.2);
  }, [isMobile]);

  return (
    <>
      <div className="w-full h-[100dvh] bg-black relative overflow-hidden cursor-grab">
        <Canvas
          camera={{
            position: [0, 0, isMobile ? 0.3 : 0.1],
            fov: isMobile ? 90 : 75,
          }}
          gl={{
            outputColorSpace: THREE.SRGBColorSpace,
            toneMapping: THREE.NoToneMapping,
          }}
         
        >
          <Scene360
            videoSrc={scenes[activeIndex].image}
            hotspots={scenes[activeIndex].hotspots}
            opacity={opacity}
            onHotspotClick={handleHotspotClick}
            rotation={scenes[activeIndex].rotation}
            controlsRef={controlsRef}
            activeIndex={activeIndex}
            isMobile={isMobile}
          />

          <OrbitControls
            ref={controlsRef}
            enableZoom={false}
            enablePan={false}
            enableDamping={true}
            dampingFactor={0.01}
            rotateSpeed={rotationSpeed}
          />
        </Canvas>

        <div
          className="absolute bottom-0 left-0 w-full h-[18%] pointer-events-none z-[1]"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0))",
          }}
        ></div>

        <a
          href="https://cynlr.com"
          target="_blank"
          className="absolute top-[5%] left-[5%] z-[3]"
        >
          <img
            src="/cynlr-logo.png"
            className="w-[100px] sm:w-[120px] md:w-[160px]"
            alt="CynLr Logo"
          />
        </a>

        <div className="absolute bottom-[5%] left-0 w-full flex justify-between items-center px-[5%] pointer-events-none z-[2]">
          <div className="pointer-events-auto text-white font-roc whitespace-normal text-lg sm:text-xl md:text-xl lg:text-2xl cursor-default">
            {scenes[activeIndex].title}
          </div>

          <div className="pointer-events-auto flex gap-2 shrink-0 sm:gap-3">
            <button
              className="bg-black/30 text-white font-roc px-3 py-2 text-xs sm:text-sm md:text-[12px] rounded-full border border-white/20 flex items-center gap-1 h-fit"
              onClick={handlePrev}
            >
              <GrPrevious /> Prev
            </button>

            <button
              className="bg-black/30 text-white font-roc px-3 py-2 text-xs sm:text-sm md:text-[12px] rounded-full border border-white/20 flex items-center gap-1 h-fit"
              onClick={handleNext}
            >
              Next <GrNext />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Scene360({
  videoSrc,
  hotspots,
  opacity,
  onHotspotClick,
  rotation = [0, 0, 0],
  controlsRef,
  activeIndex,
  isMobile,
}) {
  const [texture, setTexture] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    if (!videoSrc) return;

    setIsVideoReady(false);

    const video = document.createElement("video");
    video.src = videoSrc;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;

    let isMounted = true;

    const videoTexture = new THREE.VideoTexture(video);
    setTexture(videoTexture);

    const playVideo = async () => {
      try {
        await video.play();
        if (isMounted) setIsVideoReady(true);
      } catch (err) {
        console.log("Autoplay blocked, waiting for user gesture");
      }
    };

    playVideo();

    const handleUserGesture = () => {
      playVideo();
    };

    document.addEventListener("click", handleUserGesture, { once: true });

    return () => {
      isMounted = false;

      if (video.readyState >= 2) {
        try {
          video.pause();
        } catch {}
      }

      video.src = "";
      document.removeEventListener("click", handleUserGesture);
    };
  }, [videoSrc]);

  // CAMERA ROTATION
  useEffect(() => {
    if (!isVideoReady || !controlsRef?.current) return;

    const pitchDeg = rotation[0] || 0;
    const yawDeg = rotation[1] || 0;

    const pitch = degToRad(pitchDeg);
    const yaw = degToRad(yawDeg);

    const controls = controlsRef.current;

    controls.enableDamping = false;
    controls.dampingFactor = 0.01;

    controls.target.set(0, 0, 0);
    controls.setAzimuthalAngle(yaw);
    controls.setPolarAngle(Math.PI / 2 - pitch);

    if (activeIndex === 0) {
      controls.minAzimuthAngle = degToRad(50);
      controls.maxAzimuthAngle = degToRad(90);
    } else {
      controls.minAzimuthAngle = -Infinity;
      controls.maxAzimuthAngle = Infinity;
    }

    controls.update();
  }, [isVideoReady, rotation, controlsRef]);

  return (
    <>
      {!isVideoReady && (
        <Html
          fullscreen
          style={{
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: "100dvw",
              height: "100dvh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src="/animation.gif" width={200} height={200} alt="Loading" />
          </div>
        </Html>
      )}

      {texture && (
        <a.group>
          <mesh scale={[-1, 1, 1]}>
            <sphereGeometry
              args={[5, isMobile ? 32 : 60, isMobile ? 16 : 40]}
            />

            <a.meshBasicMaterial map={texture} side={THREE.BackSide} />
          </mesh>

          <a.group scale={opacity.to((o) => 0.9 + o * 0.1)}>
            {isVideoReady &&
              hotspots?.map((spot, i) => (
                <Hotspot
                  key={i}
                  spot={spot}
                  onClick={() => onHotspotClick(spot)}
                />
              ))}
          </a.group>
        </a.group>
      )}
    </>
  );
}

function getScaleForDistance(camera, hotspotPos, baseSize = 0.12) {
  if (!camera) return baseSize;

  const camPos = camera.position;
  const distance = camPos.distanceTo(hotspotPos);

  const scale = baseSize * distance * 0.18;

  return scale;
}

function Hotspot({ spot, onClick }) {
  const { camera } = useThree();

  const scaleValue = getScaleForDistance(
    camera,
    new THREE.Vector3(...spot.position),
    0.5
  );

  return (
    <group
      position={spot.position}
      scale={[scaleValue, scaleValue, scaleValue]}
      rotation={[
        -Math.PI / 2,
        0,
        THREE.MathUtils.degToRad(spot.iconRotation ?? 0),
      ]}
    >
      <Html transform center style={{ pointerEvents: "none" }}>
        <div 
          onClick={onClick}
          style={{
            width: '80px',
            height: '80px',
            cursor: 'pointer',
            pointerEvents: 'auto',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'scale(1.25)',
          }}
        >
          <WhiteSpinner />
        </div>
      </Html>
    </group>
  );
}